<template>
    <div
        class="preview-inline"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
    >
        <slot></slot>
        <v-menu
            v-model="showMenu"
            :position-x="menuX"
            :position-y="menuY"
            absolute
            offset-y
            :close-on-content-click="false"
            max-width="600"
            min-width="500"
        >
            <v-card @mouseenter="onMenuEnter" @mouseleave="onMenuLeave">
                <v-card-text class="pa-0 pb-2">
                    <div v-if="loading" class="text-center py-4">
                        <v-progress-circular
                            indeterminate
                            color="primary"
                            size="20"
                        ></v-progress-circular>
                        <div class="text-caption mt-1">{{ $translate('loading') }}</div>
                    </div>
                    <div v-else-if="customer" class="pa-4">
                        <div class="d-flex justify-space-between align-center mb-3">
                            <div class="copyable-row flex-grow-1" style="margin-bottom: 0;">
                                <div class="copyable-content">
                                    <router-link
                                        :to="{
                                            name: 'customer',
                                            params: { customer_id: customer.id },
                                        }"
                                        class="text-subtitle-2 font-weight-bold text-decoration-none"
                                    >
                                        {{ customer.name }}
                                    </router-link>
                                </div>
                                <v-btn
                                    x-small
                                    icon
                                    class="copy-btn"
                                    @click.stop="copyToClipboard(customer.name)"
                                >
                                    <v-icon x-small>mdi-content-copy</v-icon>
                                </v-btn>
                            </div>
                            <div class="d-flex" style="gap: 4px; flex-shrink: 0;">
                                <v-chip x-small v-if="customer.type" color="primary" outlined>
                                    {{ $utils.customerTypeText(customer.type) }}
                                </v-chip>
                                <v-chip x-small v-if="customer.entity" color="success" outlined>
                                    <v-icon x-small class="mr-1">mdi-check-circle</v-icon>
                                    {{ $translate('connected') }}
                                </v-chip>
                                <v-chip x-small v-if="customer.archived" color="grey" outlined>
                                    {{ $translate('archived') }}
                                </v-chip>
                            </div>
                        </div>
                        <div class="text-caption grey--text">
                            <div v-if="customer.code" class="mb-1 copyable-row">
                                <div class="copyable-content">
                                    <v-icon x-small class="mr-1">mdi-identifier</v-icon>
                                    <span>{{ customer.code }}</span>
                                </div>
                                <v-btn
                                    x-small
                                    icon
                                    class="copy-btn"
                                    @click.stop="copyToClipboard(customer.code)"
                                >
                                    <v-icon x-small>mdi-content-copy</v-icon>
                                </v-btn>
                            </div>
                            <div v-if="customer.siren" class="mb-1 copyable-row">
                                <div class="copyable-content">
                                    <v-icon x-small class="mr-1">mdi-domain</v-icon>
                                    <span>SIREN: {{ customer.siren }}</span>
                                </div>
                                <v-btn
                                    x-small
                                    icon
                                    class="copy-btn"
                                    @click.stop="copyToClipboard(customer.siren)"
                                >
                                    <v-icon x-small>mdi-content-copy</v-icon>
                                </v-btn>
                            </div>
                            <div v-if="customer.vat_number" class="mb-1 copyable-row">
                                <div class="copyable-content">
                                    <v-icon x-small class="mr-1">mdi-cash-register</v-icon>
                                    <span>TVA: {{ customer.vat_number }}</span>
                                </div>
                                <v-btn
                                    x-small
                                    icon
                                    class="copy-btn"
                                    @click.stop="copyToClipboard(customer.vat_number)"
                                >
                                    <v-icon x-small>mdi-content-copy</v-icon>
                                </v-btn>
                            </div>
                            <div v-if="customer.billing_address?.line1 || customer.billing_address?.city" class="mb-1 copyable-row">
                                <div class="copyable-content">
                                    <v-icon x-small class="mr-1">mdi-map-marker</v-icon>
                                    <a 
                                        :href="getGoogleMapsUrl(customer.billing_address)" 
                                        target="_blank" 
                                        class="clickable-link"
                                        @click.stop
                                    >
                                        <template v-if="customer.billing_address.line1">{{ customer.billing_address.line1 }}</template>
                                        <template v-if="customer.billing_address.line2">, {{ customer.billing_address.line2 }}</template>
                                        <template v-if="(customer.billing_address.line1 || customer.billing_address.line2) && customer.billing_address.city">, </template>
                                        <template v-if="customer.billing_address.city">{{ customer.billing_address.postal_code }} {{ customer.billing_address.city }}</template>
                                    </a>
                                </div>
                                <v-btn
                                    x-small
                                    icon
                                    class="copy-btn"
                                    @click.stop="copyToClipboard(getFullAddress(customer.billing_address))"
                                >
                                    <v-icon x-small>mdi-content-copy</v-icon>
                                </v-btn>
                            </div>
                            <template v-if="customer.billing_address?.emails && customer.billing_address.emails.length">
                                <div v-for="(email, index) in customer.billing_address.emails" :key="'email-' + index" class="mb-1 copyable-row">
                                    <div class="copyable-content">
                                        <v-icon x-small class="mr-1">mdi-email</v-icon>
                                        <a :href="'mailto:' + email" class="clickable-link" @click.stop>{{ email }}</a>
                                    </div>
                                    <v-btn
                                        x-small
                                        icon
                                        class="copy-btn"
                                        @click.stop="copyToClipboard(email)"
                                    >
                                        <v-icon x-small>mdi-content-copy</v-icon>
                                    </v-btn>
                                </div>
                            </template>
                            <div v-if="customer.billing_address?.phone_number" class="mb-1 copyable-row">
                                <div class="copyable-content">
                                    <v-icon x-small class="mr-1">mdi-phone</v-icon>
                                    <a :href="'tel:' + customer.billing_address.phone_number" class="clickable-link" @click.stop>{{ customer.billing_address.phone_number }}</a>
                                </div>
                                <v-btn
                                    x-small
                                    icon
                                    class="copy-btn"
                                    @click.stop="copyToClipboard(customer.billing_address.phone_number)"
                                >
                                    <v-icon x-small>mdi-content-copy</v-icon>
                                </v-btn>
                            </div>
                            <div v-if="customer.billing_address?.mobile_phone_number" class="mb-1 copyable-row">
                                <div class="copyable-content">
                                    <v-icon x-small class="mr-1">mdi-cellphone</v-icon>
                                    <a :href="'tel:' + customer.billing_address.mobile_phone_number" class="clickable-link" @click.stop>{{ customer.billing_address.mobile_phone_number }}</a>
                                </div>
                                <v-btn
                                    x-small
                                    icon
                                    class="copy-btn"
                                    @click.stop="copyToClipboard(customer.billing_address.mobile_phone_number)"
                                >
                                    <v-icon x-small>mdi-content-copy</v-icon>
                                </v-btn>
                            </div>
                            <div v-if="catalogs && catalogs.length" class="mb-1">
                                <div class="mb-1">
                                    <v-icon x-small class="mr-1">mdi-book-open-variant</v-icon>
                                    <span class="font-weight-medium">{{ $translate('catalogs') }} :</span>
                                </div>
                                <div v-for="catalog in displayedCatalogs" :key="catalog.id" class="ml-6 mb-1 text-caption">
                                    • {{ getCatalogName(catalog) }}
                                </div>
                                <div v-if="hasMoreCatalogs" class="ml-6 mb-1 text-caption">
                                    <a 
                                        href="#" 
                                        class="clickable-link" 
                                        @click.prevent.stop="toggleShowAllCatalogs"
                                    >
                                        {{ showAllCatalogs ? $translate('show_less') : $translate('and_more', { count: remainingCatalogsCount }) }}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else class="text-body-2 pa-4 text-center grey--text">
                        {{ $translate('no_customer_found') }}
                    </div>
                </v-card-text>
            </v-card>
        </v-menu>
    </div>
</template>

<script>
import axios from "@/plugins/axios";
import GlobalMixins from "@/mixins/global";

export default {
    name: "CustomerPreview",
    mixins: [GlobalMixins],
    props: {
        customerId: {
            type: [Number, String],
            required: true,
        },
        delay: {
            type: Number,
            default: 1000,
        },
    },
    data() {
        return {
            customer: null,
            catalogs: [],
            loading: false,
            hoverTimer: null,
            showMenu: false,
            menuX: 0,
            menuY: 0,
            showAllCatalogs: false,
            maxCatalogsToShow: 5,
        };
    },
    computed: {
        displayedCatalogs() {
            if (!this.catalogs || !this.catalogs.length) {
                return [];
            }
            if (this.showAllCatalogs) {
                return this.catalogs;
            }
            return this.catalogs.slice(0, this.maxCatalogsToShow);
        },
        hasMoreCatalogs() {
            return this.catalogs && this.catalogs.length > this.maxCatalogsToShow;
        },
        remainingCatalogsCount() {
            if (!this.catalogs) return 0;
            return this.catalogs.length - this.maxCatalogsToShow;
        },
    },
    methods: {
        onMouseEnter(event) {
            if (this.hoverTimer) {
                clearTimeout(this.hoverTimer);
            }
            
            const rect = event.currentTarget.getBoundingClientRect();
            this.menuX = rect.left + 50;
            this.menuY = rect.bottom;
            
            this.hoverTimer = setTimeout(() => {
                this.showMenu = true;
                this.loadCustomer();
            }, this.delay);
        },
        onMouseLeave() {
            if (this.hoverTimer) {
                clearTimeout(this.hoverTimer);
                this.hoverTimer = null;
            }
            
            this.hoverTimer = setTimeout(() => {
                this.showMenu = false;
            }, 200);
        },
        onMenuEnter() {
            if (this.hoverTimer) {
                clearTimeout(this.hoverTimer);
                this.hoverTimer = null;
            }
        },
        onMenuLeave() {
            this.showMenu = false;
        },
        async loadCustomer() {
            if (this.customer || this.loading) {
                return;
            }

            this.loading = true;

            try {
                const [customerResponse, catalogsResponse] = await Promise.all([
                    axios.get(`/api/customers/${this.customerId}/`),
                    axios.get('/api/catalogassignments/', {
                        params: { customer: this.customerId }
                    }).catch(() => ({ data: { results: [] } })) // Ignore errors for catalogs
                ]);
                
                this.customer = customerResponse.data;
                this.catalogs = catalogsResponse.data.results.map(item => item.catalog_data);
            } catch (error) {
                console.error('Error loading customer:', error);
                this.customer = null;
            } finally {
                this.loading = false;
            }
        },
        getGoogleMapsUrl(address) {
            if (!address) return '#';
            
            const parts = [];
            if (address.line1) parts.push(address.line1);
            if (address.line2) parts.push(address.line2);
            if (address.postal_code) parts.push(address.postal_code);
            if (address.city) parts.push(address.city);
            if (address.country) parts.push(address.country);
            
            const query = encodeURIComponent(parts.join(', '));
            return `https://www.google.com/maps/search/?api=1&query=${query}`;
        },
        getFullAddress(address) {
            if (!address) return '';
            
            const parts = [];
            if (address.line1) parts.push(address.line1);
            if (address.line2) parts.push(address.line2);
            const cityPart = [];
            if (address.postal_code) cityPart.push(address.postal_code);
            if (address.city) cityPart.push(address.city);
            if (cityPart.length) parts.push(cityPart.join(' '));
            
            return parts.join(', ');
        },
        async copyToClipboard(text) {
            try {
                await navigator.clipboard.writeText(text);
                this.addAlert({
                    text: this.$translate('copied'),
                    type: 'success',
                    timeout: 2000,
                });
            } catch (error) {
                console.error('Failed to copy:', error);
                this.addAlert({
                    text: this.$translate('copy_failed'),
                    type: 'error',
                    timeout: 2000,
                });
            }
        },
        getCatalogName(catalog) {
            if (!catalog) return '';
            if (catalog.owner === this.current_entity_id) {
                return catalog.name;
            }
            return `${catalog.owner_data?.name} - ${catalog.name}`;
        },
        toggleShowAllCatalogs() {
            this.showAllCatalogs = !this.showAllCatalogs;
        },
    },
    beforeDestroy() {
        if (this.hoverTimer) {
            clearTimeout(this.hoverTimer);
        }
    },
    i18n: {
        messages: {
            en: {
                loading: "Loading...",
                archived: "Archived",
                connected: "Connected",
                no_customer_found: "Unable to load customer information",
                catalogs: "Catalogs",
                copied: "Copied to clipboard",
                copy_failed: "Failed to copy",
                and_more: "& {count} more",
                show_less: "Show less",
            },
            fr: {
                loading: "Chargement...",
                archived: "Archivé",
                connected: "Connecté",
                no_customer_found: "Impossible de charger les informations du client",
                catalogs: "Catalogues",
                copied: "Copié dans le presse-papiers",
                copy_failed: "Échec de la copie",
                and_more: "& {count} de plus",
                show_less: "Voir moins",
            },
        },
    },
};
</script>

<style scoped>
.preview-inline {
    display: inline;
}

.v-card ::v-deep .v-card__text {
    user-select: text;
}

.clickable-link {
    color: inherit;
    text-decoration: none;
    cursor: pointer;
    user-select: text;
}

.clickable-link:hover {
    text-decoration: underline;
    color: #1976d2;
}

.selectable-text {
    user-select: text;
    cursor: text;
}

.selectable-text span {
    user-select: text;
}

.text-caption {
    user-select: text;
}

.copyable-row {
    display: flex;
    align-items: center;
    gap: 4px;
}

.copyable-content {
    display: flex;
    align-items: center;
}

.copy-btn {
    opacity: 0;
    transition: opacity 0.2s;
    flex-shrink: 0;
}

.copyable-row:hover .copy-btn {
    opacity: 1;
}

.copy-btn:hover {
    background-color: #f5f5f5 !important;
}
</style>

